import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
    SpecDetail,
    SpecType,
    SpecDetailString,
    SpecDetailStringList,
} from '../types';
import { autoResizeTextArea } from '../utils/domUtils';

interface SpecTextEditProps {
    spec: SpecDetail;
    index: number;
    additionalInputs: { [key: number]: string[] };
    onStringChange: (
        index: number,
        newText: string,
        e: React.ChangeEvent<HTMLTextAreaElement> | null,
    ) => void;
    onStringListChange: (
        specIndex: number,
        textIndex: number,
        newText: string,
        e: React.ChangeEvent<HTMLTextAreaElement> | null,
    ) => void;
    onAdditionalInputChange: (
        specIndex: number,
        inputIndex: number,
        value: string,
        e: React.ChangeEvent<HTMLTextAreaElement>,
    ) => void;
    onRemoveInputField: (specIndex: number, inputIndex: number) => void;
    onRemoveStringListItem: (specIndex: number, textIndex: number) => void;
    onSetAdditionalInputs: React.Dispatch<
        React.SetStateAction<{
            [key: number]: string[];
        }>
    >;
    // Add any other necessary props, e.g., handlers for adding/removing fields if managed here
}

const SpecTextEdit: React.FC<SpecTextEditProps> = ({
    spec,
    index,
    additionalInputs,
    onStringChange,
    onStringListChange,
    onAdditionalInputChange,
    onRemoveInputField,
    onRemoveStringListItem,
    onSetAdditionalInputs,
}) => {
    if (spec.spec_type === SpecType.SPEC_STRING) {
        const stringSpec = spec as SpecDetailString;
        const hasAdditionalInputs =
            additionalInputs[index] && additionalInputs[index].length > 0;
        const totalFieldCount = hasAdditionalInputs
            ? 1 + additionalInputs[index].length
            : 1;
        const showRemoveButtons = totalFieldCount > 1;

        return (
            <>
                <div className="input-field-container">
                    <textarea
                        className="spec-edit-textarea"
                        value={stringSpec.content.text}
                        onChange={(e) =>
                            onStringChange(index, e.target.value, e)
                        }
                        placeholder="Enter text..."
                        rows={1}
                        onFocus={(e) => autoResizeTextArea(e.target)} // Added for initial render focus
                        onInput={(e) =>
                            autoResizeTextArea(e.target as HTMLTextAreaElement)
                        } // Ensure resize on input
                    />
                    {showRemoveButtons && (
                        <button
                            className="icon-button"
                            onClick={() => {
                                if (hasAdditionalInputs) {
                                    const firstAdditionalValue =
                                        additionalInputs[index][0];
                                    onStringChange(
                                        index,
                                        firstAdditionalValue,
                                        null,
                                    );
                                    onSetAdditionalInputs((prev) => {
                                        const newInputs = { ...prev };
                                        if (newInputs[index]) {
                                            newInputs[index] =
                                                newInputs[index].slice(1);
                                            if (newInputs[index].length === 0) {
                                                delete newInputs[index];
                                            }
                                        }
                                        return newInputs;
                                    });
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faMinus} />
                        </button>
                    )}
                </div>

                {hasAdditionalInputs &&
                    additionalInputs[index].map((value, inputIndex) => (
                        <div
                            className="input-field-container"
                            key={`additional-${index}-${inputIndex}`}
                        >
                            <textarea
                                className="spec-edit-textarea"
                                value={value}
                                onChange={(e) =>
                                    onAdditionalInputChange(
                                        index,
                                        inputIndex,
                                        e.target.value,
                                        e,
                                    )
                                }
                                placeholder="Enter text..."
                                rows={1}
                                onFocus={(e) => autoResizeTextArea(e.target)}
                                onInput={(e) =>
                                    autoResizeTextArea(
                                        e.target as HTMLTextAreaElement,
                                    )
                                }
                            />
                            {showRemoveButtons && (
                                <button
                                    className="icon-button"
                                    onClick={() =>
                                        onRemoveInputField(index, inputIndex)
                                    }
                                >
                                    <FontAwesomeIcon icon={faMinus} />
                                </button>
                            )}
                        </div>
                    ))}
            </>
        );
    }

    if (spec.spec_type === SpecType.SPEC_STRING_LIST) {
        const stringListSpec = spec as SpecDetailStringList;
        const hasAdditionalInputs =
            additionalInputs[index] && additionalInputs[index].length > 0;
        const totalFieldCount =
            stringListSpec.content.text.length +
            (hasAdditionalInputs ? additionalInputs[index].length : 0);
        const showRemoveButtons = totalFieldCount > 1;

        return (
            <>
                {stringListSpec.content.text.map((text, textIndex) => (
                    <div
                        className="input-field-container"
                        key={`main-${index}-${textIndex}`}
                    >
                        <textarea
                            className="spec-edit-textarea"
                            value={text}
                            onChange={(e) =>
                                onStringListChange(
                                    index,
                                    textIndex,
                                    e.target.value,
                                    e,
                                )
                            }
                            placeholder="Enter text..."
                            rows={1}
                            onFocus={(e) => autoResizeTextArea(e.target)}
                            onInput={(e) =>
                                autoResizeTextArea(
                                    e.target as HTMLTextAreaElement,
                                )
                            }
                        />
                        {showRemoveButtons && (
                            <button
                                className="icon-button"
                                onClick={() => {
                                    if (
                                        stringListSpec.content.text.length > 1
                                    ) {
                                        onRemoveStringListItem(
                                            index,
                                            textIndex,
                                        );
                                    } else if (hasAdditionalInputs) {
                                        const firstAdditionalValue =
                                            additionalInputs[index][0];
                                        onStringListChange(
                                            index,
                                            textIndex,
                                            firstAdditionalValue,
                                            null,
                                        );
                                        onSetAdditionalInputs((prev) => {
                                            const newInputs = { ...prev };
                                            if (newInputs[index]) {
                                                newInputs[index] =
                                                    newInputs[index].slice(1);
                                                if (
                                                    newInputs[index].length ===
                                                    0
                                                ) {
                                                    delete newInputs[index];
                                                }
                                            }
                                            return newInputs;
                                        });
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faMinus} />
                            </button>
                        )}
                    </div>
                ))}

                {hasAdditionalInputs &&
                    additionalInputs[index].map((value, inputIndex) => (
                        <div
                            className="input-field-container"
                            key={`additional-${index}-${inputIndex}`}
                        >
                            <textarea
                                className="spec-edit-textarea"
                                value={value}
                                onChange={(e) =>
                                    onAdditionalInputChange(
                                        index,
                                        inputIndex,
                                        e.target.value,
                                        e,
                                    )
                                }
                                placeholder="Enter text..."
                                rows={1}
                                onFocus={(e) => autoResizeTextArea(e.target)}
                                onInput={(e) =>
                                    autoResizeTextArea(
                                        e.target as HTMLTextAreaElement,
                                    )
                                }
                            />
                            {showRemoveButtons && (
                                <button
                                    className="icon-button"
                                    onClick={() =>
                                        onRemoveInputField(index, inputIndex)
                                    }
                                >
                                    <FontAwesomeIcon icon={faMinus} />
                                </button>
                            )}
                        </div>
                    ))}
            </>
        );
    }

    return null; // Or some fallback UI if needed
};

export default SpecTextEdit;
